import { GeoClass } from '../../../../interface/response/GeoFiltersResponse';
import { useAppDispatch } from '../../../../hook/useAppDispatch';
import { setGlobalFilter } from '../../../../store/slice/global-filters';
import { t } from 'i18next';
import { useMemo, useRef } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';

interface OptionsListProps {
  column: string;
  filters: any;
  handleChange?: (col: string, option: GeoClass) => void;
  filteredOptions: GeoClass[];
  checkbox: boolean;
  isFullHeight?: boolean;
}

const OptionsList: React.FC<OptionsListProps> = ({
  column,
  filters,
  handleChange,
  filteredOptions,
  checkbox,
  isFullHeight,
}) => {
  const dispatch = useAppDispatch();
  const parentRef = useRef<HTMLDivElement>(null);

  /*
   * Initializes a virtualizer for rendering large lists efficiently.
   * Only the visible rows (plus overscan) are rendered into the DOM,
   * improving performance when the dropdown contains many options.
   */
  const rowVirtualizer = useVirtualizer({
    // Total number of items to virtualize.
    count: filteredOptions.length,

    // Returns the scrollable container element.
    getScrollElement: () => parentRef.current,

    //Estimated row height (in pixels). Used until actual height is measured.
    estimateSize: () => 40,

    // Number of extra items rendered above and below the viewport.
    overscan: 5,

    // Measures the actual height of each rendered item.
    measureElement: (el) => el.getBoundingClientRect().height,
  });

  /*
   * HEIGHT determines how much vertical space the virtualized list can use.
   * - Full height is used in Single-select mode (no extra UI below the list).
   * - A reduced height (75%) is used in Multi-select mode, where
   *   space is needed for "Select All".
   */
  const HEIGHT = isFullHeight ? '100%' : '75%';
  const virtualRows = rowVirtualizer.getVirtualItems();
  const totalHeight = rowVirtualizer.getTotalSize();

  const selectedIds = useMemo(() => {
    if (!filters[column]) return new Set<number>();
    if (Array.isArray(filters[column])) {
      return new Set(filters[column].map((f: GeoClass) => f.ID));
    }
    return new Set([filters[column].id]);
  }, [filters, column]);

  const sortedOptions = useMemo(() => {
    return [...filteredOptions].sort((a, b) => {
      const aSelected = selectedIds.has(a.ID);
      const bSelected = selectedIds.has(b.ID);

      if (aSelected && !bSelected) return -1;
      if (!aSelected && bSelected) return 1;

      return 0;
    });
  }, [filteredOptions, selectedIds]);

  if (!checkbox) {
    return (
      <div className="radio_options_list">
        {filteredOptions.map((opt) => (
          <div className="option_radio" key={opt.ID}>
            <label className="option">
              <input
                type="radio"
                value={opt.ID}
                checked={selectedIds.has(opt.ID)}
                onChange={() =>
                  dispatch(
                    setGlobalFilter({
                      key: column as any,
                      value: { id: opt.ID, name: opt.Name },
                    }),
                  )
                }
                name={'radio' + column}
              />
              {t(opt.Name)}
            </label>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div
      className="items_container"
      ref={parentRef}
      style={{
        height: HEIGHT,
        overflowY: 'auto',
        position: 'relative',
        scrollBehavior: 'auto',
      }}
    >
      <div
        style={{
          height: totalHeight,
          position: 'relative',
          width: '100%',
        }}
      >
        {virtualRows.map((virtualRow) => {
          const opt = sortedOptions[virtualRow.index];

          return (
            <div
              key={virtualRow.key}
              ref={rowVirtualizer.measureElement}
              data-index={virtualRow.index}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                transform: `translateY(${virtualRow.start}px)`,
              }}
            >
              <div key={opt.ID} className="option_checkbox">
                <input type="checkbox" checked={selectedIds.has(opt.ID)} readOnly name="checkbox" />
                <div className="option" onClick={() => handleChange?.(column, opt)}>
                  <span>{t(opt.Name)}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
export default OptionsList;
